'use client';

import { MenuFoldOutlined } from '@ant-design/icons';
import { Button, Drawer, Grid, Layout } from 'antd';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useState } from 'react';
import { ShellNav } from './ShellNav';

const MOBILE_HEADER_H = 56;

const { Sider, Content } = Layout;
const { useBreakpoint } = Grid;

export function Shell({ children }: { children: React.ReactNode }) {
    const screens = useBreakpoint();
    const isDesktop = Boolean(screens.lg);
    const [collapsed, setCollapsed] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const pathname = usePathname();
    const router = useRouter();

    const onEnter = useCallback(() => {
        if (isDesktop) setCollapsed(false);
    }, [isDesktop]);
    const onLeave = useCallback(() => {
        if (isDesktop) setCollapsed(true);
    }, [isDesktop]);

    const handleNavigate = useCallback(
        (href: string) => {
            if (href !== pathname) router.push(href);
            if (!isDesktop) setDrawerOpen(false);
        },
        [isDesktop, pathname, router],
    );

    return (
        <Layout
            style={{
                minHeight: '100dvh',
                ...(!isDesktop
                    ? { ['--app-shell-header' as string]: `calc(${MOBILE_HEADER_H}px + env(safe-area-inset-top, 0px))` }
                    : {}),
            }}
        >
            {!isDesktop && (
                <div
                    role='banner'
                    style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1000,
                        width: '100vw',
                        marginLeft: 'calc(50% - 50vw)',
                        marginRight: 'calc(50% - 50vw)',
                        height: `calc(${MOBILE_HEADER_H}px + env(safe-area-inset-top, 0px))`,
                        paddingTop: 'env(safe-area-inset-top, 0px)',
                        paddingLeft: 'calc(env(safe-area-inset-left, 0px) + 8px)',
                        paddingRight: 'calc(env(safe-area-inset-right, 0px) + 8px)',
                        backgroundColor: '#fff',
                        opacity: 1,
                        backdropFilter: 'none',
                        borderBottom: '1px solid var(--ant-color-border)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        boxSizing: 'border-box',
                    }}
                >
                    <Button
                        aria-label='Открыть меню'
                        type='default'
                        size='large'
                        icon={<MenuFoldOutlined />}
                        onClick={() => setDrawerOpen(true)}
                        style={{
                            width: 44,
                            height: 44,
                            padding: 0,
                            borderRadius: 8,
                            boxShadow: 'none',
                        }}
                    />
                    <div style={{ fontWeight: 700, fontSize: 14, letterSpacing: 0.3 }}>COASWORLD ADMIN</div>
                </div>
            )}

            {isDesktop ? (
                <Sider
                    width={240}
                    collapsedWidth={56}
                    collapsed={collapsed}
                    trigger={null}
                    onMouseEnter={onEnter}
                    onMouseLeave={onLeave}
                    theme='light'
                    style={{
                        position: 'sticky',
                        left: 0,
                        top: 0,
                        height: '100dvh',
                        borderRight: '1px solid var(--ant-color-border)',
                    }}
                >
                    <div
                        style={{
                            height: 56,
                            display: 'flex',
                            alignItems: 'center',
                            paddingInline: collapsed ? 0 : 16,
                            justifyContent: collapsed ? 'center' : 'flex-start',
                            borderBottom: '1px solid var(--ant-color-border)',
                            fontWeight: 700,
                            fontSize: 14,
                            letterSpacing: 0.3,
                        }}
                    >
                        {collapsed ? 'C' : 'COASWORLD ADMIN'}
                    </div>

                    <ShellNav currentPath={pathname} collapsed={collapsed} onNavigate={handleNavigate} />
                </Sider>
            ) : (
                <Drawer
                    placement='left'
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    width={280}
                    styles={{ header: { display: 'none' }, body: { padding: 0 } }}
                >
                    <div
                        style={{
                            height: 56,
                            display: 'flex',
                            alignItems: 'center',
                            paddingInline: 16,
                            borderBottom: '1px solid var(--ant-color-border)',
                            fontWeight: 700,
                            fontSize: 14,
                            letterSpacing: 0.3,
                        }}
                    >
                        COASWORLD ADMIN
                    </div>
                    <ShellNav currentPath={pathname} collapsed={false} onNavigate={handleNavigate} />
                </Drawer>
            )}

            <Layout>
                <Content>{children}</Content>
            </Layout>
        </Layout>
    );
}
